import React, {useMemo} from "react";
import {Col, Row} from "reactstrap";
import {Divider} from "@mui/material";
import Button from "../../../../../base/button/button";
import {formatMoney} from "../../../../../../../core/services/utils/utils";
import {NewPartSupportRequestPartInformationDataGridCellKeys} from "../../../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import ProductInformationPopover from "../../../../../app-specific/draggable-popover/product-information-popover";
import {AddPartSupportRequestProcessSteps} from "../../index";
import Utils from "../../../../../../../core/services/utils/utils";

const dataGridSaveConfig = {
    name: 'AddPartSupportRequestPartInfoSectionDataGrid',
    version: 1,
}

const AddPartSupportRequestPartInfoSection = ({
                                                  setProcess,
                                                  setLookupValues,
                                                  defineValues,
                                                  partInfoValues,
                                                  setNewRequest,
                                              }) => {

    /**
     * Removes the selectedQuantity and selected serial number from lookup values then redirects the operator to
     * lookup section of the dialog.
     */
    const previousSection = () => {
        setLookupValues(prevState => ({
            ...prevState,
            selectedQuantity: 0,
            serialNumber: prevState.showProductOrSerialNoSelection
                ? null :
                prevState.serialNumber,
        }));
        setProcess(AddPartSupportRequestProcessSteps.lookup);
    }

    /**
     * Sets the new request in the state and then redirects the operator to documentation section of the dialog.
     */
    const nextSection = () => {
        setNewRequest({
            ...(partInfoValues?.request ?? {}),
            customer: partInfoValues?.request?.customer ?? defineValues.customer,
            user: partInfoValues?.request?.user ?? defineValues.user,
        });
        setProcess(AddPartSupportRequestProcessSteps.documentation);
    }

    /**
     * Memo version of data grid column
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: "Title",
                name: NewPartSupportRequestPartInformationDataGridCellKeys.title,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                pinnedToggleable: false,
                width: 120,
            },
            {
                title: "Price",
                name: NewPartSupportRequestPartInformationDataGridCellKeys.price,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                sortable: false,
                pinnedToggleable: false,
                width: 80,
            },
        ], [])

    const [dataGridColumns] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Memo version data grid row entities
     */
    const rows = useMemo(() => {
        return partInfoValues?.extraFees?.map((extraFee) => ({
            key: extraFee.id,
            data: extraFee,
            cells: {
                [NewPartSupportRequestPartInformationDataGridCellKeys.title]: extraFee?.titleInfo?.title,
                [NewPartSupportRequestPartInformationDataGridCellKeys.price]: extraFee.isPercent
                    ? `${!isNaN(parseFloat(extraFee.value))
                        ? parseFloat(extraFee.value)?.toFixed(2)
                        : ''}`
                    : formatMoney(extraFee.value ?? '')
            }
        }))
    }, [partInfoValues?.extraFees])


    return (
        <Row className='no-gutters justify-content-center align-items-start w-100 part-information-section'>
            <div className={'w-100 row no-gutters'}>
                <>
                    <Row className={'w-100 no-gutters mb-3 p-3 '}>
                        <Col xs={5}>
                            <div className='part no-shadow'
                                 key={partInfoValues?.request?.cover?.url ?? partInfoValues?.request?.imageSource}>
                                <div className='img single warranty'>
                                    <img
                                        src={Utils.encodeFileUrl(partInfoValues?.request?.cover?.url)}
                                        alt={partInfoValues?.request.partNo}
                                        onContextMenu={(e) => e.preventDefault()}
                                        onDragStart={(e) => e.preventDefault()}
                                        className={'disable-downloading-image'}
                                    />
                                </div>
                            </div>
                        </Col>
                        <Col xs={5}>
                            <Row className='no-gutters mb-2 w-100'>
                                <Col xs={12} className='mb-1 d-flex no-gutters align-items-center justify-content-start'>
                                    <ProductInformationPopover
                                        partNo={partInfoValues?.request.partNo}
                                        buttonProps={{
                                            sx: {paddingInlineStart: 0}
                                        }}
                                    >
                                        <p className='part-number'>
                                            {partInfoValues?.request.partNo ?? ""}
                                        </p>
                                    </ProductInformationPopover>
                                    <span className={'request-type'}> ({partInfoValues?.request.type})</span>
                                </Col>
                                <Col xs={12} className='mb-2 d-flex no-gutters align-items-start justify-content-start'>
                                    <p className='serial-number'>
                                        Serial No: {partInfoValues?.request.serialNo}
                                    </p>
                                </Col>
                            </Row>
                            <Row className='no-gutters mb-2 w-100'>
                                <Col xs={12}
                                     className='mb-2 d-flex no-gutters align-items-start justify-content-start'>
                                    <p className='description'
                                       dangerouslySetInnerHTML={{__html: partInfoValues?.request.description}}/>
                                </Col>
                            </Row>
                            <Row className='no-gutters mb-2 w-100'>
                                <Col xs={12} className={'mb-2'}>
                                    <p className='extra-fees-title'>
                                        Extra Fees
                                    </p>
                                </Col>
                                {

                                    partInfoValues?.extraFees?.length > 0
                                        ? <PartnerDataGrid
                                            {...dataGridSaveConfig}
                                            initialLayoutState={{
                                                columns: columns,
                                            }}
                                            columns={dataGridColumns}
                                            rows={rows}
                                            hideFooter
                                            hideToolbar
                                            visibleRows={rows.length}
                                        />
                                        : <div className={'w-100'}>
                                            <p className={'no-extra-fees'}>
                                                There is no extra fees for this request
                                            </p>
                                        </div>
                                }
                            </Row>
                        </Col>
                    </Row>
                    <Divider className='divider'/>
                    <Row className='no-gutters footer justify-content-between align-items-start w-100'>
                        <Button
                            variant={'dialog'}
                            color={'primary'}
                            onClick={previousSection}
                            borderRadius={'0 8px 0 8px'}
                        >
                            Back
                        </Button>
                        <Button
                            variant={'dialog'}
                            color={'primary'}
                            onClick={nextSection}
                            borderRadius={'8px 0 8px 0'}
                        >
                            Next
                        </Button>
                    </Row>
                </>
            </div>
        </Row>
    );
}

export default AddPartSupportRequestPartInfoSection;
